import { MAX_ATTEMPTS, EntitiesEnum, BASE_URL } from '@/constants';
import { isRetryableError, isTokenExpired, asApiError } from '@/utils';
import { refreshAction } from '../store/slices/auth/actions/refresh.action';

const headers = { 'Content-Type': 'application/json' };
const apiUrl = BASE_URL;
import * as Sentry from '@sentry/react-router';

const handleResponse = async (response: any) => {
	if (!response.ok) {
		let errorMessage = response.statusText;
		try {
			const errBody = await response.json();
			errorMessage = errBody.message || errorMessage;
		} catch {
			/* no JSON body -> ignore */
		}
		const error: any = new Error(errorMessage);
		error.status = response.status;
		Sentry.captureException(error, {
			extra: {
				status: response.status,
				url: response.url,
			},
		});
		throw error;
	}

	if (response.status === 204) {
		return;
	}
	const text = await response.text();
	return text ? JSON.parse(text) : undefined;
};

const makeRequest = async (method: any, url: any, body: any = null, skipAuth = false) => {
	const { default: store } = await import('../store');

	if (!skipAuth) {
		const access: any = store.getState().session.access;

		if (!access) {
			throw new Error('No access token available');
		}

		if (isTokenExpired(access.payload)) {
			try {
				await store.dispatch(refreshAction()).unwrap();
			} catch {
				throw new Error('Session expired');
			}
		}
	}

	const buildOptions = () => {
		const token = (store.getState().session.access as any)?.token;
		if (!skipAuth && !token) {
			throw new Error('No access token available');
		}
		return {
			method,
			headers: skipAuth
				? headers
				: {
						...headers,
						Authorization: `Bearer ${token}`,
					},
			...(body && { body: JSON.stringify(body) }),
		};
	};

	let attempts = 0;
	let didRefresh = false;

	while (attempts < MAX_ATTEMPTS) {
		try {
			const response = await fetch(url, buildOptions());
			return await handleResponse(response);
		} catch (error: unknown) {
			if (!skipAuth && asApiError(error).status === 401 && !didRefresh) {
				didRefresh = true;
				try {
					await store.dispatch(refreshAction()).unwrap();
				} catch {
					throw new Error('Session expired');
				}
				continue;
			}

			if (!isRetryableError(error)) {
				throw error;
			}

			attempts += 1;
			console.warn(`Retry attempt #${attempts} for ${url} due to error`, error);

			if (attempts >= MAX_ATTEMPTS) {
				Sentry.captureException(error, { extra: { url, method } });
				throw error;
			}
		}
	}
};

const createQueryString = (params: any) => {
	const filtered = Object.entries(params)
		.filter(([, v]: any) => v != null)
		.map(([k, v]: any) => [k, Array.isArray(v) ? v.join(',') : v]);

	return new URLSearchParams(filtered).toString();
};

const createApiModule = (endpointName: any, entityName: any) => {
	const prototypeUrl = `${apiUrl}/${endpointName}`;

	return {
		async get(id: any) {
			try {
				return await makeRequest('GET', `${prototypeUrl}/${id}`, null, true);
			} catch (error: unknown) {
				throw new Error(`Error fetching ${entityName} with ID ${id}: ${asApiError(error).message}`);
			}
		},

		async create(data: any) {
			try {
				return await makeRequest('POST', prototypeUrl, data);
			} catch (error: unknown) {
				throw new Error(`Error creating ${entityName}: ${asApiError(error).message}`);
			}
		},

		async update(id: any, data: any) {
			try {
				return await makeRequest('PUT', prototypeUrl, { id, ...data });
			} catch (error: unknown) {
				throw new Error(`Error updating ${entityName} with ID ${id}: ${asApiError(error).message}`);
			}
		},

		async delete(id: any) {
			try {
				return await makeRequest('DELETE', `${prototypeUrl}/${id}`);
			} catch (error: unknown) {
				throw new Error(`Error deleting ${entityName} with ID ${id}: ${asApiError(error).message}`);
			}
		},

		async list(sort?: any, limit?: any, skip?: any, fields?: any) {
			try {
				const queryString = createQueryString({ sort, limit, skip, fields });
				return await makeRequest('GET', `${prototypeUrl}?${queryString}`, null, true);
			} catch (error: unknown) {
				throw new Error(`Error fetching ${entityName} list: ${asApiError(error).message}`);
			}
		},

		async filter(query?: any, sort?: any, limit?: any, skip?: any, fields?: any) {
			try {
				const queryParams: any = {};
				if (query && typeof query === 'object' && Object.keys(query).length > 0) {
					queryParams.q = JSON.stringify(query);
				}
				const queryString = createQueryString({ ...queryParams, sort, limit, skip, fields });
				return await makeRequest('GET', `${prototypeUrl}?${queryString}`, null, true);
			} catch (error: unknown) {
				throw new Error(`Error fetching filtered ${entityName}: ${asApiError(error).message}`);
			}
		},

		async bulkCreate(data: any) {
			try {
				return await makeRequest('POST', `${prototypeUrl}/all`, data);
			} catch (error: unknown) {
				throw new Error(`Error bulk creating ${entityName}: ${asApiError(error).message}`);
			}
		},

		async bulkUpdate(data: any) {
			try {
				return await makeRequest('PATCH', `${prototypeUrl}/all`, data);
			} catch (error: unknown) {
				throw new Error(`Error bulk updating ${entityName}: ${asApiError(error).message}`);
			}
		},
	};
};

export const Market = {
	...createApiModule('markets', EntitiesEnum.MARKET),
	async getActiveMarkets({ search, limit, skip, category_id }: any) {
		try {
			const queryString = createQueryString({ search, limit, skip, category_id });
			return await makeRequest('GET', `${apiUrl}/markets/active?${queryString}`);
		} catch (error: unknown) {
			throw new Error(`Error fetching ${EntitiesEnum.MARKET} list: ${asApiError(error).message}`);
		}
	},
};
export const Trade = {
	...createApiModule('trades', EntitiesEnum.USER_TRADE),

	async makeTrade(data: any) {
		try {
			return await makeRequest('POST', `${apiUrl}/trades/make`, data);
		} catch (error: unknown) {
			throw new Error(`Error make ${EntitiesEnum.USER_TRADE}: ${asApiError(error).message}`);
		}
	},
};

export const Notification = {
	...createApiModule('notifications', EntitiesEnum.NOTIFICATION),
	async createForEvent(eventId: string, data: any) {
		try {
			return await makeRequest(
				'POST',
				`${apiUrl}/notifications/event/${encodeURIComponent(eventId)}`,
				data
			);
		} catch (error: unknown) {
			throw new Error(`Error creating event notification: ${asApiError(error).message}`);
		}
	},
	async customList() {
		try {
			return await makeRequest('GET', `${apiUrl}/notifications/list`);
		} catch (error: unknown) {
			throw new Error(
				`Error fetching ${EntitiesEnum.NOTIFICATION} list: ${asApiError(error).message}`
			);
		}
	},
	async getListOpenMarket(event_id: any) {
		try {
			return await makeRequest('GET', `${apiUrl}/notifications/list-open-market/${event_id}`);
		} catch (error: unknown) {
			throw new Error(`Error fetching open market notifications: ${asApiError(error).message}`);
		}
	},

	async subscribeToOpenMarket(match_id: any) {
		try {
			return await makeRequest('POST', `${apiUrl}/notifications/subscribe-open-market/${match_id}`);
		} catch (error: unknown) {
			throw new Error(`Error subscribing to open market: ${asApiError(error).message}`);
		}
	},
};
export const Lobby = {
	...createApiModule('lobbies', EntitiesEnum.LOBBY),
	async join(data: any) {
		try {
			return await makeRequest('POST', `${apiUrl}/lobbies/join`, data);
		} catch (error: unknown) {
			throw new Error(`Error make ${EntitiesEnum.LOBBY}: ${asApiError(error).message}`);
		}
	},
	async getLobbiesByEventId(eventId: any) {
		try {
			return await makeRequest('GET', `${apiUrl}/lobbies/event/${eventId}`, null, true);
		} catch (error: unknown) {
			throw new Error(
				`Error fetching getLobbiesByEventId ${EntitiesEnum.LOBBY}: ${asApiError(error).message}`
			);
		}
	},
	async getLobbiesStats(userId: any) {
		try {
			if (userId) {
				return await makeRequest('GET', `${apiUrl}/lobbies/lobby-stats/me`, null, false);
			}
			return await makeRequest('GET', `${apiUrl}/lobbies/lobby-stats`, null, true);
		} catch (error: unknown) {
			throw new Error(
				`Error fetching getLobbiesStats ${EntitiesEnum.LOBBY}: ${asApiError(error).message}`
			);
		}
	},

	async deleteUserFromLobby(id: any) {
		try {
			return await makeRequest('DELETE', `${apiUrl}/lobbies/lobby-users/${id}`);
		} catch (error: unknown) {
			throw new Error(`Error delete user from lobby: ${asApiError(error).message}`);
		}
	},

	async updateLobbyUsers(lobbyId: any, participants: any) {
		try {
			return await makeRequest('PUT', `${apiUrl}/lobbies/lobby-users/${lobbyId}`, participants);
		} catch (error: unknown) {
			throw new Error(`Error delete user from lobby: ${asApiError(error).message}`);
		}
	},
};

export const Waitlist = createApiModule('waitlists', EntitiesEnum.WAITLIST);

export const Slate = {
	...createApiModule('slates', EntitiesEnum.SLATE),
};

export const Match = {
	...createApiModule('matches', EntitiesEnum.MATCH),
	async updateOpeningDescription(data: any) {
		try {
			return await makeRequest('PUT', `${apiUrl}/matches/opening-description`, data);
		} catch (error: unknown) {
			throw new Error(
				`Error while updating opening description match with id=${data?.match_id}: ${asApiError(error).message}`
			);
		}
	},
};

export const FeaturedImages = createApiModule('featured-images', EntitiesEnum.FEATURED_IMAGES);
export const BotTrade = createApiModule('bot-trades', EntitiesEnum.BOT_TRADES);
export const Event = {
	...createApiModule('events', EntitiesEnum.EVENT),
	async getUserEvents(userId: any) {
		try {
			return makeRequest(
				'GET',
				`${apiUrl}/events/user-events${userId ? `?userId=${encodeURIComponent(userId)}` : ''}`
			);
		} catch (error: unknown) {
			throw new Error(
				`Error fetching ${EntitiesEnum.EVENT} list for user: ${asApiError(error).message}`
			);
		}
	},

	async getUserEventsSummary(userId: any) {
		try {
			return makeRequest(
				'GET',
				`${apiUrl}/events/user-events/summary${userId ? `?userId=${encodeURIComponent(userId)}` : ''}`
			);
		} catch (error: unknown) {
			throw new Error(
				`Error fetching ${EntitiesEnum.EVENT} summary for user: ${asApiError(error).message}`
			);
		}
	},

	async join(data: any) {
		try {
			return await makeRequest('POST', `${apiUrl}/events/join`, data);
		} catch (error: unknown) {
			throw new Error(`Error make ${EntitiesEnum.EVENT}: ${asApiError(error).message}`);
		}
	},

	async refreshGamesListByCategory(catId: any) {
		try {
			return makeRequest('GET', `${apiUrl}/events/refresh-games-list/${catId}`);
		} catch {
			throw new Error(`Error fetching ${EntitiesEnum.EVENT} games list`);
		}
	},

	async getGameGroups(catId: any) {
		try {
			return makeRequest('GET', `${apiUrl}/events/game-groups/${catId}`);
		} catch {
			throw new Error(`Error while getting ${EntitiesEnum.EVENT} game groups`);
		}
	},
};

export const User = {
	...createApiModule('users', EntitiesEnum.USER),
	_cachedMe: null as any,
	_mePromise: null as any,

	async me(forceRefresh = false) {
		if (!forceRefresh && this._cachedMe) {
			return this._cachedMe;
		}

		if (!forceRefresh && this._mePromise) {
			return this._mePromise;
		}

		this._mePromise = makeRequest('GET', `${apiUrl}/auth/profile`)
			.then((response: any) => {
				this._cachedMe = response;

				if (response?.id && response?.email) {
					Sentry.setUser({
						id: response.id,
						email: response.email,
					});
				}

				return response;
			})
			.finally(() => {
				this._mePromise = null;
			});

		return this._mePromise;
	},

	async updateMyUserData(data: any) {
		try {
			return await makeRequest('PUT', `${apiUrl}/users`, data);
		} catch (error: unknown) {
			throw new Error(`Error fetching current user profile: ${asApiError(error).message}`);
		}
	},

	async listWithPnl(limit = 100, skip = 0, search = '') {
		try {
			const queryString = createQueryString({ limit, skip, search });
			return await makeRequest('GET', `${apiUrl}/users/pnl?${queryString}`);
		} catch (error: unknown) {
			throw new Error(`Error make ${EntitiesEnum.USER}: ${asApiError(error).message}`);
		}
	},

	clearCache() {
		this._cachedMe = null;
		this._mePromise = null;
	},

	clearUserData: async (userId: any) => {
		try {
			return await makeRequest('DELETE', `${apiUrl}/users/${userId}/clear-data`);
		} catch (error: unknown) {
			throw new Error(`Error make ${EntitiesEnum.USER}: ${asApiError(error).message}`);
		}
	},

	deleteUserAccount: async (userId: any) => {
		try {
			return await makeRequest('DELETE', `${apiUrl}/users/delete-account/${userId}`);
		} catch (error: unknown) {
			throw new Error(`Error make ${EntitiesEnum.USER}: ${asApiError(error).message}`);
		}
	},
};
